import { useT } from "@/lib/i18n";

export type ServiceSubcategory = { name: string; desc: string };
export type Service = {
  slug: string;
  name: string;
  desc: string;
  subcategories?: ServiceSubcategory[];
};

type RawSubcategory = { key: string; name: string; desc: string };
type RawService = {
  slug: string;
  name: string;
  desc: string;
  subcategories?: RawSubcategory[];
};

const RAW_SERVICES: RawService[] = [
  {
    slug: "ai-agents",
    name: "AI Agents",
    desc: "Autonomous systems that reason, act, and integrate with your existing workflows.",
    subcategories: [
      { key: "assistants", name: "Custom AI assistants", desc: "Domain-trained assistants for support, sales and internal ops." },
      { key: "agentic-workflows", name: "Agentic workflows", desc: "Multi-step autonomous agents that chain tools and APIs." },
      { key: "rag", name: "RAG and knowledge retrieval", desc: "Search and answer over your private data, securely." },
    ],
  },
  {
    slug: "web-applications",
    name: "Web Applications",
    desc: "SaaS platforms, admin panels, and customer portals built to scale.",
    subcategories: [
      { key: "saas", name: "Full-stack SaaS", desc: "End-to-end products with auth, billing and multi-tenancy." },
      { key: "portals", name: "Customer & partner portals", desc: "Secure dashboards and self-service experiences." },
      { key: "admin", name: "Admin & back-office", desc: "Internal tools that make operations fast and reliable." },
    ],
  },
  {
    slug: "ai-integration",
    name: "AI Integration",
    desc: "Smart insights, search, and content generation embedded in your products.",
    subcategories: [
      { key: "search", name: "Semantic search", desc: "Embeddings-based search that understands intent." },
      { key: "summaries", name: "Summaries & insights", desc: "Automatic content summarization and structured extraction." },
      { key: "copilots", name: "In-product copilots", desc: "Contextual AI features inside your existing UI." },
    ],
  },
  {
    slug: "business-intelligence",
    name: "Business Intelligence & Data",
    desc: "Dashboards, analytics, and real-time reporting that drive decisions.",
    subcategories: [
      { key: "dashboards", name: "Dashboards & reporting", desc: "Self-serve dashboards on top of your real data." },
      { key: "pipelines", name: "Data pipelines", desc: "ETL/ELT, warehousing and reliable analytics infrastructure." },
      { key: "kpis", name: "KPI modeling", desc: "Definitions, lineage and trust in the numbers." },
    ],
  },
  {
    slug: "automation",
    name: "Automation",
    desc: "No-code and low-code workflows with n8n, Make, and Zapier.",
    subcategories: [
      { key: "ops", name: "Operations automation", desc: "Glue your SaaS stack so the team stops copy-pasting." },
      { key: "integrations", name: "Custom integrations", desc: "Webhooks, APIs and event-driven pipelines." },
      { key: "rpa", name: "Process automation", desc: "Robotic process automation across legacy systems." },
    ],
  },
  {
    slug: "cloud-devops",
    name: "Cloud & DevOps",
    desc: "50+ technologies across cloud platforms, languages, and frameworks.",
    subcategories: [
      { key: "cloud", name: "Cloud architecture", desc: "AWS, GCP and Cloudflare designs built for scale and cost." },
      { key: "cicd", name: "CI/CD & zero-downtime", desc: "Pipelines, blue/green deploys and rollback safety." },
      { key: "iac", name: "Infrastructure as code", desc: "Terraform, Pulumi and reproducible environments." },
    ],
  },
  {
    slug: "security",
    name: "Security & Compliance",
    desc: "Hardening, monitoring and best practices baked in from day one.",
    subcategories: [
      { key: "hardening", name: "Hardening & audits", desc: "Reviews, threat modeling and remediation roadmaps." },
      { key: "monitoring", name: "Monitoring & alerting", desc: "Logs, metrics and on-call hygiene." },
      { key: "compliance", name: "Compliance support", desc: "GDPR, ISO and industry baselines." },
    ],
  },
  {
    slug: "support",
    name: "Managed Support",
    desc: "Production-grade support, monitoring and continuous improvement.",
    subcategories: [
      { key: "sla", name: "SLA-backed support", desc: "Defined response, resolution and escalation paths." },
      { key: "maintenance", name: "Maintenance & updates", desc: "Dependency upgrades and small enhancements." },
      { key: "oncall", name: "On-call & incident response", desc: "We carry the pager when you need us to." },
    ],
  },
  {
    slug: "industries",
    name: "Industries We Serve",
    desc: "Fintech, healthcare, e-commerce, and legal — with flexible engagement models.",
    subcategories: [
      { key: "fintech", name: "Fintech", desc: "Payments, KYC, ledger and compliance-aware systems." },
      { key: "healthcare", name: "Healthcare", desc: "Patient data, scheduling and HIPAA-aware integrations." },
      { key: "ecommerce", name: "E-commerce", desc: "Storefronts, catalogs and back-office automation." },
      { key: "legal", name: "Legal tech", desc: "Case management, document AI and client portals." },
    ],
  },
];

export function useServicesList(): Service[] {
  const { tk } = useT();
  return RAW_SERVICES.map((s) => ({
    slug: s.slug,
    name: tk(`services.${s.slug}.name`, s.name),
    desc: tk(`services.${s.slug}.desc`, s.desc),
    subcategories: s.subcategories?.map((sub) => ({
      name: tk(`services.${s.slug}.sub.${sub.key}.name`, sub.name),
      desc: tk(`services.${s.slug}.sub.${sub.key}.desc`, sub.desc),
    })),
  }));
}

export function getServicesListEs(): Service[] {
  return RAW_SERVICES.map((s) => ({
    slug: s.slug,
    name: s.name,
    desc: s.desc,
    subcategories: s.subcategories?.map(({ name, desc }) => ({ name, desc })),
  }));
}
